// TODO: replace with generated api-client service after client regeneration
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AuthControllerService } from 'src/app/api-client';
import { resolveApiBasePath } from './api-v2-base.util';

/**
 * v2 company registration payload. The manual fields (companyName, address1,
 * city, postalCode) are optional and only used — and required by the backend
 * (400 with message code `error.finaUnavailable.manualDataRequired`) — when
 * the FINA registry is unavailable.
 */
export interface CompanyRegistrationV2Resource {
  email: string;
  password: string;
  phone1: string;
  companyNo: string;
  companyName?: string;
  address1?: string;
  city?: string;
  postalCode?: string;
}

/** Response of a successful e-mail verification. */
export interface EmailVerificationResult {
  verified: boolean;
}

/**
 * Registration-page configuration. `autoApprove: false` means registrations
 * queue for manual review, so the register form shows the official-email nudge.
 */
export interface RegistrationConfig {
  autoApprove: boolean;
}

/**
 * Interim client for the v2 public endpoints.
 * Mirrors the base-path resolution of the generated api-client services.
 */
@Injectable({ providedIn: 'root' })
export class PublicV2Service {
  private readonly basePath: string;

  constructor(private http: HttpClient, apiConfig: AuthControllerService) {
    this.basePath = resolveApiBasePath(apiConfig);
  }

  /** Whether registrations are auto-approved (open-door mode) or manually reviewed. */
  public getRegistrationConfig(): Observable<RegistrationConfig> {
    return this.http.get<RegistrationConfig>(
      `${this.basePath}/api/public/registration-config`
    );
  }

  /** Register a company with the FINA-unavailable manual-data fallback. */
  public registerCompany(
    resource: CompanyRegistrationV2Resource
  ): Observable<void> {
    return this.http.post<void>(
      `${this.basePath}/api/public/v2/register/company`,
      resource
    );
  }

  /**
   * Confirm an e-mail address with the 6-digit code sent to it.
   * Returns 400 with message code `error.emailVerification.invalidToken`
   * on a wrong or expired code (surfaced by the global ErrorService).
   */
  public verifyEmail(
    email: string,
    code: string
  ): Observable<EmailVerificationResult> {
    return this.http.post<EmailVerificationResult>(
      `${this.basePath}/api/public/verify-email`,
      { email, code }
    );
  }

  /** Resend the verification e-mail. The backend always responds 200. */
  public resendVerification(email: string): Observable<void> {
    return this.http.post<void>(
      `${this.basePath}/api/public/resend-verification`,
      { email }
    );
  }

  /**
   * Fix a mistyped e-mail before it is verified: the password chosen at
   * registration proves account ownership, the address is swapped and a fresh
   * code goes to the new one. Returns 400 `error.emailVerification.invalidToken`
   * on any credential mismatch and `error.unique.userEmail` when the new
   * address is already taken.
   */
  public changeVerificationEmail(
    email: string,
    password: string,
    newEmail: string
  ): Observable<void> {
    return this.http.post<void>(
      `${this.basePath}/api/public/change-verification-email`,
      { email, password, newEmail }
    );
  }
}
